import React, { useState, useRef, useEffect } from "react";
import { apiService } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Upload,
  FileText,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Eye,
  Code,
  HelpCircle,
  ChevronRight,
  Copy,
  FileSearch,
  Layers,
  Tag,
  Calendar,
  DollarSign,
  User,
  Hash,
  Building2,
  Plus,
  Trash2,
  Edit,
  Regex,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

const FIELD_COLORS = [
  { value: "#3B82F6", label: "Blue" },
  { value: "#10B981", label: "Green" },
  { value: "#8B5CF6", label: "Violet" },
  { value: "#F59E0B", label: "Amber" },
  { value: "#6366F1", label: "Indigo" },
  { value: "#EC4899", label: "Pink" },
  { value: "#14B8A6", label: "Teal" },
  { value: "#F97316", label: "Orange" },
  { value: "#64748B", label: "Slate" },
  { value: "#EF4444", label: "Red" },
];

const DocumentAnalyzerPage = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [viewMode, setViewMode] = useState("visual"); // visual, raw
  const [selectedTransaction, setSelectedTransaction] = useState(0);
  const fileInputRef = useRef(null);
  
  // Custom fields state
  const [customFields, setCustomFields] = useState([]);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [savingField, setSavingField] = useState(false);
  const [newFieldForm, setNewFieldForm] = useState({
    field_id: "",
    label: "",
    pattern: "",
    description: "",
    color: "#6B7280",
    importance: "medium"
  });
  
  // Load custom fields on mount
  useEffect(() => {
    const loadCustomFields = async () => {
      try {
        const response = await apiService.getCustomFields();
        setCustomFields(response.data.fields || []);
      } catch (error) {
        console.error("Failed to load custom fields:", error);
      }
    };
    loadCustomFields();
  }, []);

  const handleAddCustomField = async () => {
    if (!newFieldForm.field_id || !newFieldForm.label || !newFieldForm.pattern) {
      toast.error("Field ID, Label, and Pattern are required");
      return;
    }
    setSavingField(true);
    try {
      await apiService.addCustomField(
        newFieldForm.field_id,
        newFieldForm.label,
        newFieldForm.pattern,
        newFieldForm.description,
        newFieldForm.color,
        newFieldForm.importance,
        "wise"
      );
      toast.success(`Field "${newFieldForm.label}" added`);
      setFieldDialogOpen(false);
      setNewFieldForm({ field_id: "", label: "", pattern: "", description: "", color: "#6B7280", importance: "medium" });
      // Reload custom fields
      const response = await apiService.getCustomFields();
      setCustomFields(response.data.fields || []);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to add field";
      toast.error(message);
    } finally {
      setSavingField(false);
    }
  };

  const handleDeleteCustomField = async (fieldId) => {
    try {
      await apiService.deleteCustomField(fieldId);
      toast.success("Field removed");
      setCustomFields(prev => prev.filter(f => f.field_id !== fieldId));
    } catch (error) {
      toast.error("Failed to remove field");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedFile) {
      toast.error("Please select a file first");
      return;
    }

    setAnalyzing(true);
    try {
      const response = await apiService.analyzeDocument(selectedFile);
      setResult(response.data);
      toast.success(`Analyzed ${response.data.transaction_count} transactions`);
    } catch (error) {
      const message = error.response?.data?.detail || "Analysis failed";
      toast.error(message);
    } finally {
      setAnalyzing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const getFieldIcon = (fieldId) => {
    const icons = {
      transaction_type: <Tag className="w-4 h-4" />,
      transaction_details_type: <Tag className="w-4 h-4" />,
      amount_currency: <DollarSign className="w-4 h-4" />,
      amount: <DollarSign className="w-4 h-4" />,
      running_balance: <DollarSign className="w-4 h-4" />,
      total_fees: <DollarSign className="w-4 h-4" />,
      counterparty: <Building2 className="w-4 h-4" />,
      counterparty_name: <Building2 className="w-4 h-4" />,
      merchant: <Building2 className="w-4 h-4" />,
      payer_name: <User className="w-4 h-4" />,
      payee_name: <User className="w-4 h-4" />,
      payment_date: <Calendar className="w-4 h-4" />,
      transaction_date: <Calendar className="w-4 h-4" />,
      date: <Calendar className="w-4 h-4" />,
      datetime: <Calendar className="w-4 h-4" />,
      card_identifier: <Hash className="w-4 h-4" />,
      card_last_four: <Hash className="w-4 h-4" />,
      cardholder: <User className="w-4 h-4" />,
      card_holder: <User className="w-4 h-4" />,
      transaction_id: <Hash className="w-4 h-4" />,
      transferwise_id: <Hash className="w-4 h-4" />,
      reference: <Hash className="w-4 h-4" />,
      payment_reference: <Hash className="w-4 h-4" />,
      balance: <DollarSign className="w-4 h-4" />,
      statement_line_61: <Code className="w-4 h-4" />,
      description_86: <FileText className="w-4 h-4" />,
      description: <FileText className="w-4 h-4" />,
      debit_credit: <ChevronRight className="w-4 h-4" />,
      currency: <DollarSign className="w-4 h-4" />,
      exchange_rate: <DollarSign className="w-4 h-4" />,
      exchange_from: <DollarSign className="w-4 h-4" />,
      exchange_to: <DollarSign className="w-4 h-4" />,
      note: <FileText className="w-4 h-4" />,
    };
    return icons[fieldId] || <Tag className="w-4 h-4" />;
  };

  const renderFieldDefinitions = () => {
    if (!result?.field_definitions) return null;

    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-600" />
            Field Reference Guide
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3">
            {Object.entries(result.field_definitions).map(([key, def]) => (
              <div
                key={key}
                className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border"
                style={{ borderLeftColor: def.color, borderLeftWidth: 4 }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: def.color + "20", color: def.color }}
                >
                  {getFieldIcon(key)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{def.label}</span>
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        def.importance === "high"
                          ? "border-red-300 text-red-700 bg-red-50"
                          : def.importance === "medium"
                          ? "border-amber-300 text-amber-700 bg-amber-50"
                          : "border-slate-300 text-slate-600"
                      }`}
                    >
                      {def.importance}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{def.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderTransactionAnalysis = (transaction, index) => {
    const isSelected = selectedTransaction === index;

    return (
      <div
        key={index}
        className={`border rounded-lg overflow-hidden transition-all ${
          isSelected ? "border-emerald-500 shadow-md" : "border-slate-200"
        }`}
      >
        <button
          onClick={() => setSelectedTransaction(isSelected ? -1 : index)}
          className="w-full p-4 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                transaction.is_complete
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-amber-100 text-amber-700"
              }`}
            >
              {transaction.is_complete ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                <AlertTriangle className="w-5 h-5" />
              )}
            </div>
            <div>
              <div className="font-medium text-sm">Transaction #{index + 1}</div>
              <div className="text-xs text-slate-500">
                {transaction.field_count} fields extracted
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Section Type Badge */}
            {transaction.section_type && (
              <Badge
                variant="outline"
                className={
                  transaction.section_type === "header"
                    ? "border-orange-300 text-orange-700 bg-orange-50"
                    : transaction.section_type === "transaction"
                    ? "border-blue-300 text-blue-700 bg-blue-50"
                    : "border-slate-300 text-slate-600"
                }
              >
                {transaction.section_type === "header" ? "Header/Metadata" : 
                 transaction.section_type === "transaction" ? "Transaction" : 
                 transaction.section_type}
              </Badge>
            )}
            {/* Should Ignore Badge */}
            {transaction.should_ignore && (
              <Badge variant="destructive" className="bg-red-500">
                Should Ignore
              </Badge>
            )}
            <Badge
              variant={transaction.is_complete ? "default" : "secondary"}
              className={transaction.is_complete ? "bg-emerald-600" : ""}
            >
              {transaction.is_complete ? "Complete" : "Incomplete"}
            </Badge>
            <ChevronRight
              className={`w-5 h-5 text-slate-400 transition-transform ${
                isSelected ? "rotate-90" : ""
              }`}
            />
          </div>
        </button>

        {isSelected && (
          <div className="p-4 space-y-4">
            {/* Section Classification */}
            {transaction.classification && (
              <div className={`p-3 rounded-lg border ${
                transaction.should_ignore 
                  ? "bg-red-50 border-red-200" 
                  : "bg-blue-50 border-blue-200"
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {transaction.should_ignore ? (
                    <AlertTriangle className="w-4 h-4 text-red-600" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 text-blue-600" />
                  )}
                  <span className={`font-medium text-sm ${
                    transaction.should_ignore ? "text-red-800" : "text-blue-800"
                  }`}>
                    {transaction.should_ignore 
                      ? "This section will be IGNORED during import" 
                      : "This section will be IMPORTED as a transaction"}
                  </span>
                </div>
                <p className={`text-xs ${
                  transaction.should_ignore ? "text-red-700" : "text-blue-700"
                }`}>
                  Reason: {transaction.classification.reason}
                  {transaction.classification.confidence && (
                    <span className="ml-2">
                      (Confidence: {transaction.classification.confidence}%)
                    </span>
                  )}
                </p>
                {transaction.classification.header_matches?.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    Header patterns matched: {transaction.classification.header_matches.length}
                  </p>
                )}
                {transaction.classification.transaction_matches?.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    Transaction patterns matched: {transaction.classification.transaction_matches.length}
                  </p>
                )}
              </div>
            )}

            {/* Raw Text */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Raw Text</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(transaction.raw_text)}
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copy
                </Button>
              </div>
              <div className="bg-slate-900 text-slate-100 p-3 rounded-lg text-xs font-mono overflow-x-auto">
                <pre className="whitespace-pre-wrap">{transaction.raw_text}</pre>
              </div>
            </div>

            {/* Extracted Fields */}
            <div>
              <span className="text-sm font-medium text-slate-700 mb-2 block">
                Extracted Fields
              </span>
              <div className="space-y-2">
                {transaction.fields.map((field, fIdx) => (
                  <div
                    key={fIdx}
                    className="flex items-start gap-3 p-3 rounded-lg border"
                    style={{
                      borderLeftColor: field.definition.color,
                      borderLeftWidth: 4,
                      backgroundColor: field.definition.color + "08",
                    }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: field.definition.color + "20",
                        color: field.definition.color,
                      }}
                    >
                      {getFieldIcon(field.field)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span
                          className="font-medium text-sm"
                          style={{ color: field.definition.color }}
                        >
                          {field.definition.label}
                        </span>
                        {field.csv_column && (
                          <Badge variant="outline" className="text-xs">
                            CSV: {field.csv_column}
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm font-mono mt-1 bg-white px-2 py-1 rounded border inline-block">
                        {field.value}
                      </div>
                      {field.matched_text && (
                        <div className="text-xs text-slate-400 mt-1">
                          Matched: "{field.matched_text}"
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Missing High-Priority Fields */}
            {!transaction.is_complete && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-amber-800 font-medium text-sm mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  Missing Important Fields
                </div>
                <p className="text-xs text-amber-700">
                  This transaction may be missing from imports because some required
                  fields couldn't be extracted. Check the raw text above to identify
                  the issue.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="document-analyzer-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileSearch className="w-7 h-7 text-emerald-600" />
            Document Structure Analyzer
          </h1>
          <p className="text-slate-500 mt-1">
            Upload a Wise PDF, Wise CSV, or ING MT940 file to visualize and debug the parsed structure
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Upload & Controls */}
          <div className="lg:col-span-1 space-y-4">
            {/* Upload Card */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Upload Document</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    selectedFile
                      ? "border-emerald-300 bg-emerald-50"
                      : "border-slate-300 hover:border-emerald-400 hover:bg-slate-50"
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.940,.mt940,.csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Upload
                    className={`w-8 h-8 mx-auto mb-2 ${
                      selectedFile ? "text-emerald-600" : "text-slate-400"
                    }`}
                  />
                  {selectedFile ? (
                    <div>
                      <p className="font-medium text-emerald-700">{selectedFile.name}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-slate-600">
                        Drop file here or click to browse
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Wise PDF, Wise CSV, or ING MT940
                      </p>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleAnalyze}
                  disabled={!selectedFile || analyzing}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="analyze-btn"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Layers className="w-4 h-4 mr-2" />
                      Analyze Structure
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Custom Fields Card */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Regex className="w-4 h-4 text-violet-600" />
                  Custom Fields
                </CardTitle>
                <Dialog open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline" className="h-8">
                      <Plus className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Custom Field</DialogTitle>
                      <DialogDescription>
                        Define a new field to extract from documents using a regex pattern.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Field ID</Label>
                          <Input
                            placeholder="e.g., payment_reference"
                            value={newFieldForm.field_id}
                            onChange={(e) => setNewFieldForm({ ...newFieldForm, field_id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Label</Label>
                          <Input
                            placeholder="e.g., Payment Reference"
                            value={newFieldForm.label}
                            onChange={(e) => setNewFieldForm({ ...newFieldForm, label: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Regex Pattern</Label>
                        <Input
                          placeholder="e.g., reference:\s*(.+?)(?:\s|$)"
                          value={newFieldForm.pattern}
                          onChange={(e) => setNewFieldForm({ ...newFieldForm, pattern: e.target.value })}
                          className="font-mono text-sm"
                        />
                        <p className="text-xs text-slate-500">
                          Use capturing groups () to extract the value. First group will be used.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Input
                          placeholder="What this field contains..."
                          value={newFieldForm.description}
                          onChange={(e) => setNewFieldForm({ ...newFieldForm, description: e.target.value })}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Color</Label>
                          <Select value={newFieldForm.color} onValueChange={(v) => setNewFieldForm({ ...newFieldForm, color: v })}>
                            <SelectTrigger>
                              <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: newFieldForm.color }} />
                                <SelectValue />
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              {FIELD_COLORS.map(c => (
                                <SelectItem key={c.value} value={c.value}>
                                  <div className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded" style={{ backgroundColor: c.value }} />
                                    {c.label}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Importance</Label>
                          <Select value={newFieldForm.importance} onValueChange={(v) => setNewFieldForm({ ...newFieldForm, importance: v })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="low">Low</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setFieldDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleAddCustomField} disabled={savingField}>
                        {savingField ? "Saving..." : "Add Field"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {customFields.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No custom fields defined. Add fields to extract additional data.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {customFields.map((field) => (
                      <div
                        key={field.field_id}
                        className="flex items-center justify-between p-2 rounded-lg border bg-slate-50"
                        style={{ borderLeftColor: field.color, borderLeftWidth: 3 }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: field.color + "20", color: field.color }}>
                            <Regex className="w-3 h-3" />
                          </div>
                          <div>
                            <div className="text-sm font-medium">{field.label}</div>
                            <div className="text-xs text-slate-400 font-mono truncate max-w-[150px]" title={field.patterns?.[0]}>
                              {field.patterns?.[0]}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-slate-400 hover:text-red-500"
                          onClick={() => handleDeleteCustomField(field.field_id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Summary Card */}
            {result && (
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Analysis Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Format</span>
                    <Badge variant="outline">{result.format}</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Bank</span>
                    <Badge className="bg-blue-600">{result.bank}</Badge>
                  </div>
                  {result.columns && result.columns.length > 0 && (
                    <div className="pt-2 border-t">
                      <span className="text-sm text-slate-500 block mb-2">CSV Columns Detected</span>
                      <div className="flex flex-wrap gap-1">
                        {result.columns.slice(0, 8).map((col, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {col}
                          </Badge>
                        ))}
                        {result.columns.length > 8 && (
                          <Badge variant="outline" className="text-xs">
                            +{result.columns.length - 8} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="text-sm text-slate-500">Total Sections</span>
                    <span className="font-medium">{result.transaction_count}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Actual Transactions</span>
                    <span className="font-medium text-blue-600">
                      {result.actual_transactions || result.transaction_count}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Headers/Metadata</span>
                    <span className="font-medium text-orange-600">
                      {result.header_sections || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="text-sm text-slate-500">Will be Ignored</span>
                    <span className="font-medium text-red-600">
                      {result.ignored_sections || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Complete & Valid</span>
                    <span className="font-medium text-emerald-600">
                      {result.complete_transactions}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Field Reference */}
            {result && renderFieldDefinitions()}
          </div>

          {/* Right Column - Results */}
          <div className="lg:col-span-2">
            {!result ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-16">
                  <div className="text-center text-slate-400">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No document analyzed yet</p>
                    <p className="text-sm mt-1">
                      Upload a Wise PDF or ING MT940 file to see the structure
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* View Toggle */}
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Transaction Analysis</h2>
                  <Select value={viewMode} onValueChange={setViewMode}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="visual">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          Visual
                        </div>
                      </SelectItem>
                      <SelectItem value="raw">
                        <div className="flex items-center gap-2">
                          <Code className="w-4 h-4" />
                          Raw Text
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {viewMode === "visual" ? (
                  <ScrollArea className="h-[calc(100vh-280px)]">
                    <div className="space-y-3 pr-4">
                      {result.transactions.map((tx, idx) =>
                        renderTransactionAnalysis(tx, idx)
                      )}
                    </div>
                  </ScrollArea>
                ) : (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">Raw Document Text</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(result.raw_text_preview)}
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <ScrollArea className="h-[calc(100vh-350px)]">
                        <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs font-mono whitespace-pre-wrap">
                          {result.raw_text_preview}
                        </pre>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default DocumentAnalyzerPage;
